import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { computeEnabledFeatures } from '../features'
import { trackEvent } from '../analytics'
import { conflictCheckedUpdate } from '../utils'

function mapUserSettings(data) {
  return {
    id: data.id,
    subscriptionTier: data.subscription_tier || "free",
    stripeCustomerId: data.stripe_customer_id || null,
    stripeSubscriptionId: data.stripe_subscription_id || null,
    subscriptionStatus: data.subscription_status || null,
    gantt: data.feature_gantt ?? true,
    changeOrders: data.feature_change_orders ?? true,
    budget: data.feature_budget ?? true,
    dailyLogs: data.feature_daily_logs ?? true,
    reports: data.feature_reports ?? true,
    contacts: data.feature_contacts ?? true,
    templates: data.feature_templates ?? true,
    nlQuery: data.feature_nl_query ?? true,
    punchList: data.feature_punch_list ?? true,
    calendar: data.feature_calendar ?? true,
    nlqV2: data.feature_nlq_v2 ?? false,
    hasApiKey: data.anthropic_api_key_encrypted != null,
    seatAddonCount: data.seat_addon_count ?? 0,
  }
}

function mapCompanyProfile(data) {
  return {
    id: data.id,
    companyName: data.company_name || "",
    addressLine1: data.address_line1 || "",
    addressLine2: data.address_line2 || "",
    city: data.city || "",
    state: data.state || "",
    zip: data.zip || "",
    phone: data.phone || "",
    email: data.email || "",
    website: data.website || "",
    licenseNumber: data.license_number || "",
    logoUrl: data.logo_url || "",
  }
}

function mapUserProfile(data) {
  return {
    id: data.id,
    firstName: data.first_name || "",
    lastName: data.last_name || "",
    phone: data.phone || "",
  }
}

export function useSettings(session, showToast) {
  const [userSettings, setUserSettings] = useState(null)
  const [companyProfile, setCompanyProfile] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [projectGroups, setProjectGroups] = useState([])

  const enabledFeatures = computeEnabledFeatures(userSettings)

  useEffect(() => {
    if (!session) return
    supabase.from("user_profile").select("*").maybeSingle()
      .then(({ data }) => { if (data) setUserProfile(mapUserProfile(data)) })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id])

  useEffect(() => {
    if (!session) return
    supabase.from("company_profile").select("*").maybeSingle()
      .then(({ data }) => { if (data) setCompanyProfile(mapCompanyProfile(data)) })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id])

  useEffect(() => {
    if (!session) return
    supabase.from("user_settings").select("*").maybeSingle()
      .then(({ data }) => { if (data) setUserSettings(mapUserSettings(data)) })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id])

  useEffect(() => {
    if (!session) return
    supabase.from("project_groups").select("*").order("sort_order")
      .then(({ data }) => {
        if (data) {
          setProjectGroups(data.map(g => ({
            id: g.id, name: g.name, sortOrder: g.sort_order ?? 0, createdAt: g.created_at, updatedAt: g.updated_at || null,
          })))
        }
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id])

  function getUserDisplayName() {
    if (userProfile && (userProfile.firstName || userProfile.lastName)) {
      return `${userProfile.firstName} ${userProfile.lastName}`.trim()
    }
    return session?.user?.email || ""
  }

  async function saveUserProfile(profile) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const row = {
      user_id: user.id,
      first_name: profile.firstName || "",
      last_name: profile.lastName || "",
      phone: profile.phone || "",
      updated_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from("user_profile").upsert(row, { onConflict: "user_id" }).select().single()
    if (error) { console.error("Failed to save user profile:", error); showToast("Failed to save profile.", "error"); return }
    setUserProfile(mapUserProfile(data))
  }

  async function saveCompanyProfile(profile) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const row = {
      user_id: user.id,
      company_name: profile.companyName || "",
      address_line1: profile.addressLine1 || "",
      address_line2: profile.addressLine2 || "",
      city: profile.city || "",
      state: profile.state || "",
      zip: profile.zip || "",
      phone: profile.phone || "",
      email: profile.email || "",
      website: profile.website || "",
      license_number: profile.licenseNumber || "",
      logo_url: profile.logoUrl || "",
      updated_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from("company_profile").upsert(row, { onConflict: "user_id" }).select().single()
    if (error) { console.error("Failed to save company profile:", error); showToast("Failed to save company profile.", "error"); return }
    setCompanyProfile(mapCompanyProfile(data))
  }

  async function saveUserSettings(settings) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const row = {
      user_id: user.id,
      subscription_tier: settings.subscriptionTier || "free",
      feature_gantt: settings.gantt ?? true,
      feature_change_orders: settings.changeOrders ?? true,
      feature_budget: settings.budget ?? true,
      feature_daily_logs: settings.dailyLogs ?? true,
      feature_reports: settings.reports ?? true,
      feature_contacts: settings.contacts ?? true,
      feature_templates: settings.templates ?? true,
      feature_nl_query: settings.nlQuery ?? true,
      feature_punch_list: settings.punchList ?? true,
      feature_calendar: settings.calendar ?? true,
      feature_nlq_v2: settings.nlqV2 ?? false,
      updated_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from("user_settings").upsert(row, { onConflict: "user_id" }).select().single()
    if (error) { console.error("Failed to save user settings:", error); showToast("Failed to save settings.", "error"); return }
    trackEvent('settings_saved')
    setUserSettings(mapUserSettings(data))
  }

  async function createProjectGroup(name) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const maxOrder = projectGroups.reduce((max, g) => Math.max(max, g.sortOrder), -1)
    const { data, error } = await supabase.from("project_groups").insert({
      user_id: user.id, name, sort_order: maxOrder + 1,
    }).select().single()
    if (error) { console.error("Failed to create group:", error); return null }
    const newGroup = { id: data.id, name: data.name, sortOrder: data.sort_order, createdAt: data.created_at }
    setProjectGroups(gs => [...gs, newGroup])
    return newGroup
  }

  async function renameProjectGroup(groupId, newName) {
    if (!newName || !newName.trim()) return
    const existing = projectGroups.find(g => g.id === groupId)
    const result = await conflictCheckedUpdate(supabase, "project_groups", groupId, { name: newName.trim() }, existing?.updatedAt)
    if (result.conflict) { return }
    if (!result.ok) { console.error("Failed to rename group:", result.error); return }
    setProjectGroups(gs => gs.map(g => g.id === groupId ? { ...g, name: newName.trim(), updatedAt: result.data.updated_at } : g))
  }

  async function deleteProjectGroup(groupId) {
    const { error } = await supabase.from("project_groups").delete().eq("id", groupId)
    if (error) { console.error("Failed to delete group:", error); return }
    setProjectGroups(gs => gs.filter(g => g.id !== groupId))
  }

  async function assignProjectToGroup(projectId, groupId, projects) {
    const supabaseId = projects.find(p => p.id === projectId)?.supabaseId || projectId
    const { error } = await supabase.from("projects").update({ group_id: groupId || null }).eq("id", supabaseId)
    if (error) { console.error("Failed to assign project to group:", error) }
    return !error
  }

  function incrementSeatAddonCount() {
    setUserSettings(prev => prev ? { ...prev, seatAddonCount: (prev.seatAddonCount || 0) + 1 } : prev)
  }

  function reset() {
    setUserSettings(null)
    setCompanyProfile(null)
    setUserProfile(null)
    setProjectGroups([])
  }

  return {
    userSettings, companyProfile, userProfile,
    projectGroups,
    enabledFeatures,
    getUserDisplayName,
    saveUserProfile, saveCompanyProfile, saveUserSettings,
    createProjectGroup, renameProjectGroup, deleteProjectGroup, assignProjectToGroup,
    incrementSeatAddonCount,
    reset,
  }
}
